import React, {useCallback} from 'react';
import {Button, Dropdown, Icon, Label, DropdownItem, Grid} from '@albert-io/atomic';
import {capitalize} from 'lodash';
import classnames from 'classnames';

import './global-assignments.scss';

import type {GlobalAssignmentFilterObj} from '../constants';
import { GlobalAssignmentsSharedOptions} from '../constants';

import ClassFilter from './ClassFilter.react';

interface AssignmentFiltersProps {
  filters: GlobalAssignmentFilterObj;
  setFilters: (value: any) => void;
  setFiltersToUse: (value: any) => void;
  assignmentsLoading: boolean;
  isMobile?: boolean;
}

const AssignmentFilters: React.FC<AssignmentFiltersProps> = ({
  setFilters,
  filters,
  setFiltersToUse,
  assignmentsLoading,
  isMobile
}) => {
  const onClickFilterOption = useCallback(
    (prop, option) => {
      if (prop === 'status' || prop === 'shared') {
        if (filters[prop] === option) {
          setFilters({...filters, [prop]: null});
        } else {
          setFilters({...filters, [prop]: option});
        }
      } else if (filters[prop][option.id]) {
        const newClasses = filters[prop];
        delete newClasses[option.id];
        setFilters({...filters, [prop]: newClasses});
      } else {
        setFilters({...filters, [prop]: {...filters[prop], [option.id]: option.name}});
      }
    },
    [filters, setFilters]
  );

  return (
    <div className='global-assignments__filters'>
      <Grid.Container className='global-assignments__filters-grid-container'>
        <Grid.Row>
          <Grid.Col all={4} s={12}>
            <div className='mb-x1 flex items-center justify-between'>
              <Label>Status</Label>
              {filters.status !== '' && filters.status !== null && (
                <button
                  className='global-assignments__filters-clear'
                  type='button'
                  onClick={() =>
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      status: ''
                    }))
                  }
                >
                  Clear
                </button>
              )}
            </div>
            <Dropdown
              trigger={
                <Button
                  className='font-normal!'
                  color='secondary'
                  variant='outlined'
                  data-testid='status-trigger'
                >
                  {capitalize(filters.status || '')}
                  <Icon icon='caret-down' />
                </Button>
              }
              position='bottom-start'
              withMaxHeight
            >
              <div>
                {['open', 'closed', 'scheduled'].map((status) => (
                  <DropdownItem
                    bold={false}
                    onClick={() => onClickFilterOption('status', status)}
                    data-testid={`${status}-option`}
                    key={status}
                  >
                    <Icon
                      icon='check'
                      className={classnames('fa-fw mr-x2', {
                        'opacity-100': filters.status === status,
                        'opacity-0': filters.status !== status
                      })}
                    />

                    {capitalize(status)}
                  </DropdownItem>
                ))}
              </div>
            </Dropdown>
          </Grid.Col>
          <Grid.Col all={4} s={12}>
            <ClassFilter setFilters={setFilters} filters={filters} isMobile={isMobile} />
          </Grid.Col>
          <Grid.Col all={4} s={12}>
            <div className='mb-x1 flex items-center justify-between'>
              <Label>Created by</Label>
              {filters.shared !== '' && filters.shared !== null && (
                <button
                  className='global-assignments__filters-clear'
                  type='button'
                  onClick={() =>
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      shared: ''
                    }))
                  }
                >
                  Clear
                </button>
              )}
            </div>
            <Dropdown
              trigger={
                <Button
                  className='font-normal!'
                  color='secondary'
                  variant='outlined'
                  data-testid='shared-trigger'
                >
                  {filters.shared}
                  <Icon icon='caret-down' />
                </Button>
              }
              position='bottom-start'
              withMaxHeight
            >
              <div>
                {GlobalAssignmentsSharedOptions.map((shared) => (
                  <DropdownItem
                    bold={false}
                    onClick={() => onClickFilterOption('shared', shared)}
                    data-testid={`${shared}-option`}
                    key={shared}
                  >
                    <Icon
                      icon='check'
                      className={classnames('fa-fw mr-x2', {
                        'opacity-100': filters.shared === shared,
                        'opacity-0': filters.shared !== shared
                      })}
                    />
                    {shared}
                  </DropdownItem>
                ))}
              </div>
            </Dropdown>
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>
      <Button
        color='primary'
        onClick={() => {
          setFiltersToUse(filters);
        }}
        disabled={assignmentsLoading}
        data-testid='apply-filters'
      >
        Apply
      </Button>
    </div>
  );
};

export default AssignmentFilters;
